import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRightIcon, CheckCircleIcon, ClipboardDocumentIcon, ExclamationTriangleIcon, EyeIcon, EyeSlashIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { API_URL } from '../../services/api';

const readError = async (response, fallback) => {
  try {
    const body = await response.json();
    if (typeof body.detail === 'string') return body.detail;
    if (body.detail?.message) return body.detail.message;
    if (Array.isArray(body.detail)) return body.detail.map((item) => item.msg).join(', ');
  } catch {
    // Use the fallback when the server did not return JSON.
  }
  return fallback;
};

const DemoActivation = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [usernameCopied, setUsernameCopied] = useState(false);
  const [form, setForm] = useState({ password: '', password_confirm: '' });

  useEffect(() => {
    let cancelled = false;
    const loadDetails = async () => {
      if (!token) {
        setError('This activation link is missing its security token.');
        setLoading(false);
        return;
      }
      try {
        const response = await fetch(`${API_URL}/api/demo-requests/activate?token=${encodeURIComponent(token)}`);
        if (!response.ok) throw new Error(await readError(response, 'This activation link is invalid or expired.'));
        const data = await response.json();
        if (!cancelled) setDetails(data);
      } catch (err) {
        if (!cancelled) setError(err.message || 'This activation link is invalid or expired.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadDetails();
    return () => { cancelled = true; };
  }, [token]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setError('');
  };

  const handleCopyUsername = async () => {
    if (!success?.username) return;
    try {
      await navigator.clipboard.writeText(success.username);
      setUsernameCopied(true);
      window.setTimeout(() => setUsernameCopied(false), 2000);
    } catch {
      setError('Could not copy the username. Please select it manually.');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (form.password !== form.password_confirm) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/demo-requests/activate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, ...form }),
      });
      if (!response.ok) throw new Error(await readError(response, 'We could not activate this demo.'));
      setSuccess(await response.json());
    } catch (err) {
      setError(err.message || 'We could not activate this demo.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50 dark:bg-gray-950">
      <div className="w-full max-w-md">
        <div className="mb-5 text-center">
          <Link to="/" className="inline-flex items-baseline">
            <span className="text-3xl font-bold leading-none bg-gradient-to-r from-orange-500 via-orange-600 to-amber-500 bg-clip-text text-transparent tracking-tight" style={{ fontFamily: "'Baloo Bhai 2', sans-serif", fontWeight: 700 }}>Ospito</span>
            <span className="text-xl font-bold leading-none bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent -ml-0.5 -translate-y-1.5" style={{ fontFamily: "'Baloo Bhai 2', sans-serif", fontWeight: 700 }}>+</span>
          </Link>
          <p className="mt-1 text-[11px] font-medium tracking-wide text-gray-500 dark:text-gray-400">Hospital Management System</p>
        </div>

        <div className="relative overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-xl dark:border-gray-800 dark:bg-gray-900">
          <div className="p-6">
            {loading && (
              <div className="py-8 text-center">
                <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-teal-500 border-t-transparent" />
                <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">Checking your activation link…</p>
              </div>
            )}

            {!loading && error && !details && (
              <div className="py-3 text-center">
                <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50 dark:bg-amber-900/30">
                  <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                </div>
                <h1 className="mt-3 text-base font-bold text-gray-900 dark:text-white">Activation link unavailable</h1>
                <p className="mt-2 text-[13px] leading-relaxed text-gray-600 dark:text-gray-400">{error}</p>
                <Link to="/" className="mt-5 inline-flex items-center justify-center rounded-lg bg-teal-600 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-teal-700">
                  Return to Ospito+
                </Link>
              </div>
            )}

            {!loading && details && !success && (
              <>
                <div className="mb-5 flex flex-col items-center gap-2 text-center">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-teal-50 dark:bg-teal-900/30">
                    <LockClosedIcon className="h-5 w-5 text-teal-600 dark:text-teal-400" />
                  </div>
                  <div>
                    <h1 className="text-base font-bold text-gray-900 dark:text-white">Activate your demo</h1>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Set a password for your administrator account</p>
                  </div>
                </div>

                <div className="mb-5 rounded-xl border border-gray-200/80 bg-gray-50 px-3.5 py-3 dark:border-gray-800 dark:bg-gray-800/60">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">{details.facility_name}</p>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Administrator: {details.admin_full_name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Username: <strong className="text-gray-700 dark:text-gray-300">{details.admin_username}</strong></p>
                </div>

                {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300">{error}</div>}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="password" className="mb-1.5 block text-xs font-semibold text-gray-700 dark:text-gray-300">Create password</label>
                    <p className="mb-1.5 text-[11px] text-gray-400 dark:text-gray-500">Use uppercase, lowercase, a number, and a special character.</p>
                    <div className="relative">
                      <input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        value={form.password}
                        onChange={handleChange}
                        minLength={8}
                        maxLength={128}
                        required
                        autoComplete="new-password"
                        className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 pr-10 text-sm text-gray-900 outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                        placeholder="At least 8 characters"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((current) => !current)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 transition-colors hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        {showPassword ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="password_confirm" className="mb-1.5 block text-xs font-semibold text-gray-700 dark:text-gray-300">Confirm password</label>
                    <div className="relative">
                      <input
                        id="password_confirm"
                        name="password_confirm"
                        type={showPasswordConfirm ? 'text' : 'password'}
                        value={form.password_confirm}
                        onChange={handleChange}
                        minLength={8}
                        maxLength={128}
                        required
                        autoComplete="new-password"
                        className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 pr-10 text-sm text-gray-900 outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                        placeholder="Enter the password again"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswordConfirm((current) => !current)}
                        aria-label={showPasswordConfirm ? 'Hide confirmed password' : 'Show confirmed password'}
                        className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 transition-colors hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        {showPasswordConfirm ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex w-full items-center justify-center gap-1 rounded-lg bg-teal-600 px-3 py-2.5 text-xs font-semibold text-white transition-colors hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {submitting ? 'Activating…' : 'Activate demo account'}
                    {!submitting && <ArrowRightIcon className="h-3.5 w-3.5" />}
                  </button>
                </form>

                <p className="mt-4 text-center text-[11px] text-gray-400 dark:text-gray-500">
                  Need help?{' '}
                  <a href="mailto:support@ospitohms.com" className="font-medium text-teal-600 hover:underline dark:text-teal-400">support@ospitohms.com</a>
                </p>
              </>
            )}

            {success && (
              <div className="py-3 text-center">
                <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-xl bg-teal-50 dark:bg-teal-900/30">
                  <CheckCircleIcon className="h-5 w-5 text-teal-600 dark:text-teal-400" />
                </div>
                <h2 className="mt-3 text-base font-bold text-gray-900 dark:text-white">Your demo is ready</h2>
                <p className="mt-2 text-[13px] leading-relaxed text-gray-600 dark:text-gray-400">Use your email or username and the password you created to sign in.</p>
                <div className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-gray-200/80 bg-gray-50 px-3 py-2.5 text-left dark:border-gray-800 dark:bg-gray-800/60">
                  <div className="min-w-0">
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">Username</p>
                    <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">{success.username}</p>
                  </div>
                  <button
                    type="button"
                    onClick={handleCopyUsername}
                    className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-white px-2.5 py-1.5 text-[11px] font-semibold text-gray-600 shadow-sm ring-1 ring-gray-200 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:bg-gray-700 dark:text-gray-300 dark:ring-gray-600 dark:hover:bg-gray-600 dark:hover:text-white"
                  >
                    <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                    {usernameCopied ? 'Copied' : 'Copy'}
                  </button>
                </div>
                {error && <p className="mt-2 text-left text-[11px] text-red-600 dark:text-red-400">{error}</p>}
                <a
                  href="/login"
                  className="mt-4 inline-flex w-full items-center justify-center gap-1 rounded-lg bg-teal-600 px-3 py-2.5 text-xs font-semibold text-white transition-colors hover:bg-teal-700"
                >
                  Go to login
                  <ArrowRightIcon className="h-3.5 w-3.5" />
                </a>
                <p className="mt-4 text-[11px] text-gray-400 dark:text-gray-500">
                  Need help?{' '}
                  <a href="mailto:support@ospitohms.com" className="font-medium text-teal-600 hover:underline dark:text-teal-400">support@ospitohms.com</a>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoActivation;
